import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, CircularProgress, FormControl, Autocomplete, TextField, Snackbar, Typography, Alert, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { getGroups } from '../api/groups';
import { createPaymentLink } from '../api/variants';
import { getContact, sendWhatsApp } from '../api/contacts';
import { GroupInterface, ContactInterface, PaymentLinkToken } from '../types';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

interface SendPaymentLinkModalProps {
  open: boolean;
  onClose: () => void;
  contactId: string | null;
}

interface VariantOption {
  id: string;
  name: string;
  groupName: string;
  groupId: string;
}

interface SelectedItem {
  variant: VariantOption;
  price: string;
  receipt_line_details: string;
  note: string;
}

function SendPaymentLinkModal({ open, onClose, contactId }: SendPaymentLinkModalProps): React.JSX.Element {
  const [groups, setGroups] = useState<GroupInterface[]>([]);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [ddlInputValue, setDdlInputValue] = useState('');
  const [receiptForName, setReceiptForName] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('הקישור הועתק בהצלחה!');
  const [contact, setContact] = useState<ContactInterface | null>(null);

  useEffect(() => {
    if (open) {
      setPaymentLoading(true);
      getGroups()
        .then((res) => {
          setGroups(res.data || []);
        })
        .finally(() => setPaymentLoading(false));

      if (contactId) {
        getContact(contactId).then(res => {
          setContact(res);
        }).catch(console.error);
      }
    } else {
      setSelectedItems([]);
      setReceiptForName('');
      setPaymentUrl('');
      setSubmitting(false);
      setContact(null);
    }
  }, [open, contactId]);

  const variantOptions: VariantOption[] = groups.flatMap(group =>
    (group.variants || []).map(variant => ({
      id: variant.id,
      name: variant.name,
      groupName: group.name,
      groupId: group.id
    }))
  );

  const handleAddVariant = (variant: VariantOption | null) => {
    if (!variant) return;
    // Allow adding the same variant multiple times
    setSelectedItems(prev => [...prev, { variant, price: '', receipt_line_details: '', note: '' }]);
  };

  const handleRemoveItem = (index: number) => {
    setSelectedItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: 'price' | 'receipt_line_details' | 'note', value: string) => {
    setSelectedItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const handlePaymentSubmit = async () => {
    setSubmitting(true);
    try {
      const payload: PaymentLinkToken = {
        items: selectedItems.map(item => ({
          variant_id: item.variant.id,
          price: Number(item.price) || 0,
          receipt_line_details: item.receipt_line_details,
          note: item.note || undefined
        })),
        contact_id: Number(contactId),
        receipt_for_name: receiptForName,
      };

      const res = await createPaymentLink(payload);
      if (res && res.token) {
        const url = `${process.env.REACT_APP_PAYMENT_LINK_URL || ''}?token=${res.token}`;
        setPaymentUrl(url);
      }
    } catch (error) {
      console.error("Failed to create payment link", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = () => {
    if (paymentUrl) {
      navigator.clipboard.writeText(paymentUrl);
      setToastMessage('הקישור הועתק בהצלחה!');
      setToastOpen(true);
    }
  };

  const handleWhatsAppSend = async () => {
    if (!contact || !contact.phone) return;
    try {
      setSubmitting(true);
      await sendWhatsApp(contact.phone, `היי ${contact.parent_name}, מצורף קישור לתשלום: ${paymentUrl}`);
      setToastMessage('הקישור נשלח לווצאפ בהצלחה!');
      setToastOpen(true);
    } catch (error) {
      console.error(error);
      setToastMessage('שגיאה בשליחה לווצאפ');
      setToastOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>שליחת קישור לתשלום</DialogTitle>
        <DialogContent>
          <Typography fontSize={'14px'}>חשוב לוודא תקינות של גיל טלפון ושם</Typography>
          {paymentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="שם לקבלה"
                value={receiptForName}
                onChange={(e) => setReceiptForName(e.target.value)}
                fullWidth
                disabled={submitting || !!paymentUrl}
              />

              <FormControl fullWidth>
                <Autocomplete
                  options={variantOptions}
                  getOptionLabel={(option) => `${option.name} (${option.groupName})`}
                  value={null}
                  inputValue={ddlInputValue}
                  onInputChange={(event, newValue, reason) => {
                    if (reason === 'reset') {
                      setDdlInputValue('');
                    } else {
                      setDdlInputValue(newValue);
                    }
                  }}
                  onChange={(event, newValue) => {
                    handleAddVariant(newValue);
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={submitting || !!paymentUrl}
                  blurOnSelect
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="הוסף קורס"
                      placeholder="בחר קורס..."
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props as any;
                    return (
                      <li key={key || option.id} {...rest}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{option.name}</div>
                          <div style={{ fontSize: '0.8em', color: 'gray' }}>{option.groupName}</div>
                        </div>
                      </li>
                    );
                  }}
                  noOptionsText="לא נמצאו קורסים"
                />
              </FormControl>

              {selectedItems.map((item, index) => (
                <Box key={index} sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" fontWeight="bold">{item.variant.name} ({item.variant.groupName})</Typography>
                    {!paymentUrl && (
                      <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)} disabled={submitting}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="מחיר"
                      type="number"
                      size="small"
                      value={item.price}
                      onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                      disabled={submitting || !!paymentUrl}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="פרטים לחשבונית"
                      size="small"
                      value={item.receipt_line_details}
                      onChange={(e) => handleItemChange(index, 'receipt_line_details', e.target.value)}
                      disabled={submitting || !!paymentUrl}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <TextField
                    label="הערה"
                    size="small"
                    value={item.note}
                    onChange={(e) => handleItemChange(index, 'note', e.target.value)}
                    disabled={submitting || !!paymentUrl}
                    fullWidth
                  />
                </Box>
              ))}

              {paymentUrl && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Box
                    onClick={handleCopy}
                    sx={{
                      p: 2,
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '85%'
                      }}
                    >
                      {paymentUrl}
                    </Typography>
                    <ContentCopyIcon fontSize="small" color="action" />
                  </Box>
                  {contact?.phone && (
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<WhatsAppIcon />}
                      onClick={handleWhatsAppSend}
                      disabled={submitting}
                      sx={{ mt: 1 }}
                    >
                      שלח קישור לווצאפ
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>סגור</Button>
          {!paymentUrl && (
            <Button onClick={handlePaymentSubmit} variant="contained" color="primary" disabled={paymentLoading || submitting || selectedItems.length === 0}>
              {submitting ? <CircularProgress size={20} color="inherit" /> : 'אישור'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar open={toastOpen} autoHideDuration={3000} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setToastOpen(false)} severity={toastMessage.includes('שגיאה') ? 'error' : 'success'} sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default SendPaymentLinkModal;
